'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'

interface DeviceConfig {
  videoId: string
  audioId: string
}

interface DeviceTestScreenProps {
  onConfirm: (config: DeviceConfig) => void
  interviewInfo?: {
    candidateName?: string
    jobTitle?: string | null
  }
}

const DeviceTestScreen: React.FC<DeviceTestScreenProps> = ({ onConfirm, interviewInfo }) => {
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([])
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedVideoId, setSelectedVideoId] = useState<string>('')
  const [selectedAudioId, setSelectedAudioId] = useState<string>('')

  const [volume, setVolume] = useState(0)
  const [isConnReady, setIsConnReady] = useState(false)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationRef = useRef<number | null>(null)

  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const v = devices.filter((d) => d.kind === 'videoinput')
      const a = devices.filter((d) => d.kind === 'audioinput')
      setVideoDevices(v)
      setAudioDevices(a)

      if (v.length > 0 && !selectedVideoId) setSelectedVideoId(v[0].deviceId)
      if (a.length > 0 && !selectedAudioId) setSelectedAudioId(a[0].deviceId)
    } catch (err) {
      void err
    }
  }, [selectedVideoId, selectedAudioId])

  const triggerPermissionRefresh = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.set('retryMediaPermission', Date.now().toString())
    window.location.assign(url.toString())
  }, [])

  const initPreview = useCallback(async () => {
    setIsLoading(true)
    setIsConnReady(false)

    // Clean up previous stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }

    // Clean up previous AudioContext if closed or closing
    if (audioCtxRef.current && audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = null
      analyserRef.current = null
    }

    try {
      const constraints: MediaStreamConstraints = {
        video: selectedVideoId ? { deviceId: { exact: selectedVideoId } } : true,
        audio: selectedAudioId
          ? { deviceId: { exact: selectedAudioId }, echoCancellation: true, noiseSuppression: true }
          : { echoCancellation: true, noiseSuppression: true },
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream
      setHasPermission(true)

      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }

      // Audio Monitoring - create new AudioContext if needed
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
      }

      // Resume context if suspended (browser policy)
      if (audioCtxRef.current.state === 'suspended') {
        await audioCtxRef.current.resume()
      }

      // Only create audio nodes if context is running
      if (audioCtxRef.current.state === 'running') {
        const source = audioCtxRef.current.createMediaStreamSource(stream)
        const analyser = audioCtxRef.current.createAnalyser()
        analyser.fftSize = 256
        source.connect(analyser)
        analyserRef.current = analyser

        const bufferLength = analyser.frequencyBinCount
        const dataArray = new Uint8Array(bufferLength)

        const updateVolume = () => {
          if (
            !analyserRef.current ||
            !audioCtxRef.current ||
            audioCtxRef.current.state !== 'running'
          ) {
            return
          }
          analyserRef.current.getByteFrequencyData(dataArray)

          // Calculate peak volume for better visual feedback
          let max = 0
          for (let i = 0; i < bufferLength; i++) {
            if (dataArray[i] > max) max = dataArray[i]
          }

          // Normalize to 0-100
          const level = (max / 255) * 100
          setVolume(level)
          animationRef.current = requestAnimationFrame(updateVolume)
        }

        updateVolume()
      }

      refreshDevices()

      // Simulate connectivity check
      setTimeout(() => setIsConnReady(true), 1500)
    } catch (err) {
      setHasPermission(false)
    } finally {
      setIsLoading(false)
    }
  }, [selectedVideoId, selectedAudioId, refreshDevices])

  useEffect(() => {
    initPreview()
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop())
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close()
      }
    }
  }, [selectedVideoId, selectedAudioId, initPreview])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('retryMediaPermission')) {
      return
    }

    url.searchParams.delete('retryMediaPermission')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  }, [])

  const handleConfirm = () => {
    // Stop preview stream before moving on
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close()
    }

    onConfirm({
      videoId: selectedVideoId,
      audioId: selectedAudioId,
    })
  }

  const micStatusLabel = volume > 5 ? 'Microphone ready' : 'Waiting for audio'
  const connectionStatusLabel = isConnReady ? 'Connection ready' : 'Checking connection'

  return (
    <div className="w-full min-h-screen bg-[#eef3ed] text-[#18211b] font-sans selection:bg-brand-500/20 flex flex-col">
      <header
        className="w-full border-b border-[#40584c] py-4 px-6 flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'rgb(43, 71, 66)' }}
      >
        <img
          src="/logo-new-light.png"
          alt="BO-SMART Logo"
          width="auto"
          height="44"
          className="h-10 object-contain"
        />
      </header>
      <div className="h-[10px] w-full bg-[#d2f34c] flex-shrink-0" />

      <div className="container mx-auto px-4 sm:px-6 flex-1 flex flex-col items-center justify-center py-8 sm:py-12 relative overflow-y-auto">
        <div className="absolute inset-x-0 top-0 h-[42vh] bg-[#466d56] -z-10 pointer-events-none" />
        <div className="absolute top-10 left-[8%] w-16 h-16 rounded-full bg-[#d2f34c]/10 -z-10 pointer-events-none" />
        <div className="absolute top-24 right-[7%] w-20 h-20 rounded-full bg-[#d2f34c]/12 -z-10 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-[58vh] bg-[#eef3ed] -z-10 pointer-events-none" />

        <div className="w-full max-w-6xl rounded-[22px] border border-[#d8ddd7] bg-[#f7f8f4] shadow-[0_18px_48px_rgba(33,48,40,0.10)] my-auto overflow-hidden">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
            <section className="px-6 py-8 sm:px-8 sm:py-10 md:px-10 md:py-12 border-b border-[#d8ddd7] lg:border-b-0 lg:border-r lg:border-[#d8ddd7]">
              <div className="mb-6">
                <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#667065]">
                  Device Check
                </p>
                <h1 className="mt-4 text-[2rem] sm:text-[2.5rem] leading-tight font-semibold tracking-tight text-[#18211b]">
                  Check your setup
                </h1>
                <p className="mt-4 max-w-lg text-sm sm:text-[15px] leading-7 text-[#5f675f]">
                  {interviewInfo?.jobTitle
                    ? `Preparing for ${interviewInfo.jobTitle}. Confirm your camera and microphone before you join.`
                    : 'Confirm your camera and microphone before you join the interview.'}
                </p>
              </div>

              <div className="relative overflow-hidden rounded-[24px] border border-[#d8ddd7] bg-[#18211b] aspect-video min-h-[320px]">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover transform scale-x-[-1]"
                />

                <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/45 via-black/15 to-transparent" />
                <div className="absolute top-5 left-5 right-5 flex flex-wrap gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/35 px-3 py-1.5 backdrop-blur-md">
                    <span
                      className={`h-2 w-2 rounded-full ${volume > 5 ? 'bg-[#d2f34c]' : 'bg-white/40'}`}
                    />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90">
                      {micStatusLabel}
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/35 px-3 py-1.5 backdrop-blur-md">
                    <span
                      className={`h-2 w-2 rounded-full ${isConnReady ? 'bg-[#d2f34c]' : 'bg-white/40'}`}
                    />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90">
                      {connectionStatusLabel}
                    </span>
                  </div>
                </div>

                {isLoading && hasPermission !== false && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#18211b]/82 text-center px-6">
                    <div className="w-12 h-12 rounded-full border-4 border-[#d2f34c]/35 border-t-[#d2f34c] animate-spin" />
                    <p className="mt-4 text-sm font-medium text-white">Initializing camera</p>
                    <p className="mt-1 text-xs text-white/65">
                      Grant access if your browser asks for permission.
                    </p>
                  </div>
                )}

                {!hasPermission && hasPermission !== null && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#18211b] text-center px-8">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500/12 border border-red-500/20">
                      <svg
                        className="w-7 h-7 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-white">
                      Camera or microphone blocked
                    </h3>
                    <p className="mt-2 max-w-sm text-sm leading-6 text-white/65">
                      Refresh and allow access from the browser prompt. If you already clicked
                      Block, enable camera and microphone in the browser address bar first.
                    </p>
                    <button
                      onClick={triggerPermissionRefresh}
                      className="mt-6 rounded-xl bg-[#d2f34c] px-5 py-3 text-sm font-semibold text-[#244034] transition-colors hover:bg-[#c6ea36]"
                    >
                      Allow Access
                    </button>
                    <button
                      onClick={triggerPermissionRefresh}
                      className="mt-3 text-xs font-medium text-white/55 underline underline-offset-4 transition-colors hover:text-white/80"
                    >
                      Reload Page
                    </button>
                  </div>
                )}
              </div>
            </section>

            <section className="px-6 py-8 sm:px-8 sm:py-10 md:px-10 md:py-12 bg-[#f3f4ef]">
              <div className="max-w-md">
                <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#7c857c]">
                  Readiness
                </p>
                <h2 className="mt-4 text-2xl font-semibold tracking-tight text-[#18211b]">
                  Everything should feel clear and calm
                </h2>
                <p className="mt-3 text-sm leading-6 text-[#5f675f]">
                  Adjust your devices below, then start when your connection and microphone are
                  ready.
                </p>
              </div>

              <div className="mt-8 space-y-6">
                <div className="rounded-2xl border border-[#d8ddd7] bg-white/75 p-5">
                  <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.2em] text-[#7c857c]">
                    <span>Mic level</span>
                    <span className={volume > 5 ? 'text-[#466d56]' : 'text-[#7c857c]'}>
                      {volume > 5 ? 'Good' : 'Idle'}
                    </span>
                  </div>
                  <div className="mt-4 h-2 rounded-full bg-[#e2e7df] overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-75 ${volume > 5 ? 'bg-[#466d56]' : 'bg-[#b7c0b7]'}`}
                      style={{ width: `${Math.max(4, volume)}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="camera-select"
                      className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#7c857c] flex items-center"
                    >
                      <svg
                        className="w-3.5 h-3.5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      Camera
                    </label>
                    <select
                      id="camera-select"
                      value={selectedVideoId}
                      onChange={(e) => setSelectedVideoId(e.target.value)}
                      className="w-full rounded-xl border border-[#d8ddd7] bg-white px-4 py-3 text-sm text-[#18211b] outline-none transition focus:border-[#466d56] focus:ring-2 focus:ring-[#466d56]/10 appearance-none cursor-pointer"
                    >
                      {videoDevices.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || 'Default Camera'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="microphone-select"
                      className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#7c857c] flex items-center"
                    >
                      <svg
                        className="w-3.5 h-3.5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                        />
                      </svg>
                      Microphone
                    </label>
                    <select
                      id="microphone-select"
                      value={selectedAudioId}
                      onChange={(e) => setSelectedAudioId(e.target.value)}
                      className="w-full rounded-xl border border-[#d8ddd7] bg-white px-4 py-3 text-sm text-[#18211b] outline-none transition focus:border-[#466d56] focus:ring-2 focus:ring-[#466d56]/10 appearance-none cursor-pointer"
                    >
                      {audioDevices.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || 'Default Mic'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <button
                  onClick={handleConfirm}
                  disabled={!hasPermission || !isConnReady}
                  className={`
                    w-full py-4 rounded-xl font-semibold text-sm uppercase tracking-[0.04em] transition-all duration-300 flex items-center justify-center gap-2
                    ${
                      hasPermission && isConnReady
                        ? 'bg-[#d2f34c] text-[#244034] shadow-[0_4px_12px_rgba(210,243,76,0.3)] hover:bg-[#c6ea36] hover:-translate-y-0.5'
                        : 'bg-[#dfe4dc] text-[#8a9389] cursor-not-allowed border border-[#d2d8d0]'
                    }
                  `}
                >
                  {isConnReady ? (
                    <>Start Interview</>
                  ) : (
                    <>
                      <div className="w-4 h-4 border-2 border-[#8a9389]/50 border-t-[#8a9389] rounded-full animate-spin" />
                      Checking Connection...
                    </>
                  )}
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeviceTestScreen
